import { TransactionError, useAddress } from '@thirdweb-dev/react';
import React, { FC } from 'react';

import { ExternalLink } from '@app/components/ExternalLink';
import { SubmitButton } from '@app/components/SubmitButton';
import { showErrorToast, showSuccessToast } from '@app/components/Toast';
import fromExponential from 'from-exponential';
import { CustomConnectWalletButton } from '@app/components/CustomConnectWalletButton';
import { NetworkMismatchButton } from '@app/components/NetworkMismatchButton';
import { useNetworkMismatch } from '@app/hooks/thirdweb/useNetworkMismatch';

interface Props {
  allowance: number | undefined;
  amount: string;
  isWithdrawInProgress: boolean;
  isApproveInProgress: boolean;
  withdrawLiquidity: () => Promise<any>;
  approveWithdraw: () => Promise<any>;
  onClose: () => void;
  balance: number;
  getWithdrawBalance: () => Promise<number | void>;
}

export const WithdrawLiquiditySubmitButton: FC<Props> = ({
  onClose,
  approveWithdraw,
  withdrawLiquidity,
  allowance,
  amount,
  isWithdrawInProgress,
  isApproveInProgress,
  balance,
  getWithdrawBalance
}) => {
  const address = useAddress();
  const isConnected = !!address;
  const isMismatchedNetwork = useNetworkMismatch();

  if (!isConnected) {
    return <CustomConnectWalletButton />;
  }

  if (isMismatchedNetwork) {
    return <NetworkMismatchButton />;
  }

  if (allowance !== undefined && +fromExponential(allowance) >= +amount) {
    return (
      <SubmitButton
        disabled={+amount > balance}
        isLoading={isWithdrawInProgress}
        onClick={async () => {
          try {
            const res = await withdrawLiquidity();

            if (res['reason'] !== undefined) {
              showErrorToast(res.reason);
            } else {
              const txHash = res?.receipt?.transactionHash;

              showSuccessToast(
                `Successfully withdrawn ${amount} from pair`,
                <ExternalLink txHash={txHash} />
              );

              await getWithdrawBalance();

              onClose();
            }
          } catch (e) {
            if (e instanceof TransactionError) {
              showErrorToast(e.reason);
            } else {
              showErrorToast(`Error withdraw ${amount} from pair`);
            }
          }
        }}
        label="Withdraw"
        loadingText="Processing..."
      />
    );
  }

  return (
    <SubmitButton
      disabled={
        allowance === undefined || allowance > +amount || +amount > balance
      }
      isLoading={isApproveInProgress}
      onClick={async () => {
        try {
          const res = await approveWithdraw();

          if (res['reason'] !== undefined) {
            showErrorToast(res.reason);
          } else {
            const txHash = res?.receipt?.transactionHash;

            showSuccessToast(
              `Successfully approved withdraw ${amount} from pair`,
              <ExternalLink txHash={txHash} />
            );
          }
        } catch (e) {
          if (e instanceof TransactionError) {
            showErrorToast(e.reason);
          } else {
            showErrorToast(`Error approving withdraw ${amount} from pair`);
          }
        }
      }}
      label="Approve withdraw"
      loadingText="Processing..."
    />
  );
};
